// src/utils/geoRisk.js
// Geo-risk calculation for multi-asset portfolios.
// Supports stocks, ETFs, crypto (BTC mining distribution), commodities, and forex positions.
//
// Both Dashboard.jsx and WorldMonitor.jsx import from here — keep exports stable.
//
// calcGeoRiskScore(holdings)         — legacy sync, uses static TICKER_GEO map
// calcForexGeoRisk(forexPositions)   — sync, maps base/quote currencies to zones
// getAssetGeoExposure(holding)       — async, per-holding geo exposure { countryName: weight }
// calculatePortfolioGeoRisk(holdings) — async, full portfolio weighted score 0–100

import { supabase } from '../lib/supabaseClient';
import { FX_CURRENCY_ZONES } from '../data/forexPairs';

// ── Static stock geo map (legacy) ─────────────────────────
// Zone IDs match CONFLICT_ZONES[].id in WorldMonitor.jsx
export const TICKER_GEO = {
  AAPL: ["taiwan", "southchina"],
  MSFT: ["ukraine"],
  XOM:  ["mideast", "iran"],
  JPM:  ["ukraine", "mideast"],
  NVDA: ["taiwan", "southchina"],
  AMZN: ["mideast"],
  TSM:  ["taiwan", "southchina"],
  V:    ["ukraine", "mideast"],
  PG:   ["ukraine"],
  META: ["ukraine"],
  SHEL: ["mideast", "iran"],
  CVX:  ["mideast", "iran", "venezuela"],
  BA:   ["ukraine", "taiwan"],
  GS:   ["ukraine", "mideast"],
  LMT:  ["ukraine", "mideast", "taiwan"],
  RTX:  ["ukraine", "mideast"],
  NOC:  ["ukraine", "mideast"],
  GD:   ["ukraine", "mideast"],
};

// Risk level → numeric score (matches WorldMonitor RISK_ORDER scale)
const RISK_SCORE = { critical: 100, high: 70, medium: 40, low: 15, none: 0 };

// Zone ID → risk level (subset — derived from WorldMonitor CONFLICT_ZONES)
const ZONE_RISK = {
  ukraine:    "critical",
  mideast:    "critical",
  taiwan:     "high",
  southchina: "high",
  sahel:      "high",
  northkorea: "high",
  iran:       "high",
  sudan:      "high",
  venezuela:  "medium",
  myanmar:    "medium",
  ethiopia:   "medium",
  pakistan:   "medium",
  haiti:      "medium",
  drcongo:    "medium",
  georgia:    "medium",
  libya:      "medium",
};

// ── 24h cache for BTC mining data ─────────────────────────
let _btcMiningCache = null;
let _btcMiningCacheTs = 0;
const BTC_CACHE_MS = 24 * 60 * 60 * 1000;

async function fetchBTCMiningDistribution() {
  if (_btcMiningCache && Date.now() - _btcMiningCacheTs < BTC_CACHE_MS) {
    return _btcMiningCache;
  }
  try {
    // Cambridge CBECI — country hashrate share
    const res = await fetch('https://ccaf.io/cbeci/api/countries', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('CBECI unavailable');
    const json = await res.json();
    // Returns array of { country, share } — normalise to { countryName: weight }
    const result = {};
    (json?.data ?? json ?? []).forEach(d => {
      if (d.country && d.share != null) result[d.country] = d.share;
    });
    if (Object.keys(result).length > 0) {
      _btcMiningCache = result;
      _btcMiningCacheTs = Date.now();
      return result;
    }
  } catch {
    // Fallback: known approximate distribution (2024 data)
  }
  const fallback = {
    'United States': 37.8,
    'China':         21.1,
    'Kazakhstan':    13.2,
    'Canada':         6.5,
    'Russia':         4.7,
    'Germany':        3.1,
    'Other':         13.6,
  };
  _btcMiningCache = fallback;
  _btcMiningCacheTs = Date.now();
  return fallback;
}

// ── Commodity geo exposure from Supabase ──────────────────
async function fetchCommodityGeoExposure(ticker) {
  try {
    if (!supabase) return null;
    const { data, error } = await supabase
      .from('commodity_geo_risk')
      .select('country_exposures')
      .eq('ticker', ticker)
      .single();
    if (error || !data) return null;
    return data.country_exposures ?? null;
  } catch {
    return null;
  }
}

// ── In-memory session cache for AI geo exposures ──────────
// Prevents repeat API calls for the same ticker within a session.
const _aiExposureCache = new Map(); // ticker → { exposures, ts }
const AI_SESSION_TTL = 30 * 60 * 1000; // 30 min

// ── getAssetGeoExposure ────────────────────────────────────
/**
 * Returns { countryName: weight (0–100) } for a holding,
 * or null if geo exposure cannot be determined.
 *
 * @param {{ ticker: string, assetType?: string, coinCapId?: string }} holding
 * @param {string} [jwt] — Supabase JWT for authenticated /api/geo-ticker calls
 * @returns {Promise<Record<string, number>|null>}
 */
export async function getAssetGeoExposure(holding, jwt) {
  const { ticker, assetType } = holding;

  // Crypto: use BTC mining distribution for BTC/BTC-USD; others → null
  if (assetType === 'crypto') {
    const isBTC = ticker === 'BTC-USD' || ticker === 'BTC';
    if (!isBTC) return null;
    try {
      return await fetchBTCMiningDistribution();
    } catch {
      return null;
    }
  }

  // Commodity: lookup in Supabase
  if (assetType === 'commodity') {
    return fetchCommodityGeoExposure(ticker);
  }

  // Stock / ETF: check static TICKER_GEO first (instant, no API cost)
  const zones = TICKER_GEO[ticker];
  if (zones?.length) {
    // Return zone IDs as pseudo-country keys — consumed by the static path in
    // calculatePortfolioGeoRisk which maps zone IDs → ZONE_RISK directly.
    const result = {};
    zones.forEach(z => { result[z] = 100 / zones.length; });
    return result;
  }

  // Not in static map → call /api/geo-ticker (Groq, Supabase-cached 7 days)
  // Check session cache first to avoid hammering the API mid-calculation.
  const cached = _aiExposureCache.get(ticker);
  if (cached && Date.now() - cached.ts < AI_SESSION_TTL) {
    return cached.exposures;
  }

  try {
    const headers = { 'Content-Type': 'application/json' };
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;

    const res = await fetch(`/api/geo-ai?ticker=${encodeURIComponent(ticker)}`, { headers });
    if (!res.ok) return null;

    const { exposures } = await res.json();
    if (!exposures || Object.keys(exposures).length === 0) return null;

    _aiExposureCache.set(ticker, { exposures, ts: Date.now() });
    return exposures;
  } catch {
    return null;
  }
}

// ── calculatePortfolioGeoRisk ──────────────────────────────
/**
 * Async version of calcGeoRiskScore that supports all asset types.
 * Fetches country_zone_mappings from Supabase to compute risk.
 * Unknown stocks are sent to /api/geo-ticker (Groq, 7-day Supabase cache).
 * Falls back to calcGeoRiskScore on any unrecoverable error (missing tables, network failure, etc.).
 *
 * @param {Array<{ ticker: string, weight: number, assetType?: string }>} holdings
 * @param {string} [jwt] — Supabase JWT, forwarded to /api/geo-ticker for auth
 * @returns {Promise<number|null>}
 */
export async function calculatePortfolioGeoRisk(holdings, jwt) {
  if (!holdings?.length) return null;

  try {
    // Load country → zone mappings and zone intensity overrides from Supabase in parallel.
    let zoneMappings = null;
    let zoneOverrides = {}; // zone_id → intensity multiplier (0.5–1.5, default 1.0)
    if (supabase) {
      const [mappingsRes, overridesRes] = await Promise.all([
        supabase.from('country_zone_mappings').select('country_name, zone_id'),
        supabase.from('zone_risk_overrides').select('zone_id, intensity'),
      ]);
      if (!mappingsRes.error && mappingsRes.data?.length) {
        zoneMappings = {};
        mappingsRes.data.forEach(r => { zoneMappings[r.country_name] = r.zone_id; });
      }
      if (!overridesRes.error && overridesRes.data?.length) {
        overridesRes.data.forEach(r => { zoneOverrides[r.zone_id] = r.intensity; });
      }
    }

    let weightedSum = 0;
    let totalWeight = 0;

    await Promise.all(holdings.map(async h => {
      const weight = (h.weight ?? 0) / 100;
      if (weight <= 0) return;

      const assetType = h.assetType ?? 'stock';

      // ── Path A: Stock/ETF in static TICKER_GEO — no API call needed ──────
      if ((assetType === 'stock' || assetType === 'etf') && TICKER_GEO[h.ticker]?.length) {
        const zones = TICKER_GEO[h.ticker];
        // Apply AI-derived intensity multiplier per zone (default 1.0 = no adjustment).
        // effectiveScore = baseScore × intensity, capped at 100.
        const zoneScore = zones.reduce((sum, zoneId) => {
          const base      = RISK_SCORE[ZONE_RISK[zoneId] ?? 'none'] ?? 0;
          const intensity = zoneOverrides[zoneId] ?? 1.0;
          return sum + base * intensity;
        }, 0) / zones.length;
        weightedSum += weight * Math.min(100, zoneScore);
        totalWeight += weight;
        return;
      }

      // ── Path B: Unknown stock/ETF → AI exposure, or crypto/commodity → own source ──
      // getAssetGeoExposure handles routing: unknown stock → /api/geo-ticker,
      // BTC → CBECI mining data, commodity → Supabase commodity_geo_risk table.
      const exposure = await getAssetGeoExposure(h, jwt).catch(() => null);

      if (!exposure || Object.keys(exposure).length === 0 || !zoneMappings) {
        // No data available — exclude from both numerator and denominator so
        // unknown holdings don't dilute the score of known risky positions.
        return;
      }

      totalWeight += weight;

      const totalExp = Object.values(exposure).reduce((s, v) => s + v, 0) || 1;
      let holdingScore = 0;
      Object.entries(exposure).forEach(([country, share]) => {
        const zoneId    = zoneMappings[country];
        const level     = zoneId ? (ZONE_RISK[zoneId] ?? 'none') : 'none';
        const intensity = zoneId ? (zoneOverrides[zoneId] ?? 1.0) : 1.0;
        holdingScore   += (share / totalExp) * (RISK_SCORE[level] ?? 0) * intensity;
      });
      weightedSum += weight * Math.min(100, holdingScore);
    }));

    if (totalWeight <= 0) return 0;
    return Math.round(weightedSum / totalWeight);
  } catch {
    // Supabase tables don't exist yet — fall back to sync version
    return calcGeoRiskScore(holdings);
  }
}

// ── calcForexGeoRisk (sync) ───────────────────────────────
/**
 * Calculate geo-risk score for a set of open forex positions.
 * Uses the base currency of each position to look up zone exposure.
 * Unknown forex positions contribute 0 to the denominator (same as stock rule).
 *
 * @param {Array<{ base: string, quote: string, notional: number }>} forexPositions
 * @returns {number|null} — 0-100, or null if no positions
 */
export function calcForexGeoRisk(forexPositions) {
  if (!forexPositions?.length) return null;
  let weightedSum = 0, totalNotional = 0;

  forexPositions.forEach(pos => {
    const notional = pos.notional ?? 0;
    if (notional <= 0) return;

    // Use base currency zones (the currency being bought/sold)
    const zones = FX_CURRENCY_ZONES[pos.base] ?? [];
    if (zones.length === 0) return; // unknown geo exposure — exclude

    const score = zones.reduce(
      (sum, zoneId) => sum + (RISK_SCORE[ZONE_RISK[zoneId] ?? "none"] ?? 0), 0
    ) / zones.length;

    weightedSum   += notional * score;
    totalNotional += notional;
  });

  if (totalNotional <= 0) return 0;
  return Math.round(weightedSum / totalNotional);
}

// ── calcGeoRiskScore (legacy sync) ────────────────────────
/**
 * Calculate geo-risk score for a portfolio using the static TICKER_GEO map.
 * Returns 0–100, or null if there are no holdings.
 * Used by Dashboard.jsx and WorldMonitor.jsx — do not remove.
 *
 * @param {Array<{ ticker: string, weight: number }>} holdings
 * @returns {number|null}
 */
export function calcGeoRiskScore(holdings) {
  if (!holdings?.length) return null;

  let weightedSum = 0;
  let totalWeight = 0;

  holdings.forEach(h => {
    const zones = TICKER_GEO[h.ticker] ?? [];
    // Unknown holdings (not in TICKER_GEO) are excluded from both numerator and
    // denominator so they don't dilute the score of known risky positions.
    if (zones.length === 0) return;

    // Blended average across all exposed zones — not worst-case only.
    // XOM: mideast(100) + iran(70) + venezuela(40) → 70, not 100.
    const score  = zones.reduce((sum, zoneId) => sum + (RISK_SCORE[ZONE_RISK[zoneId] ?? "none"] ?? 0), 0) / zones.length;
    const weight = h.weight ?? 0;
    weightedSum += (weight / 100) * score;
    totalWeight += weight / 100;
  });

  if (totalWeight <= 0) return 0;
  return Math.round(weightedSum / totalWeight);
}
